import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { CalendarIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Label } from '@/components/ui/label';
import { TimePicker } from '@/components/ui/time-picker';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { getDateLocale } from '@/utils/dateLocale';

interface OneTimeDateTimeStepProps {
  date: Date | null;
  onDateChange: (date: Date | null) => void;
  startTime: string;
  onStartTimeChange: (time: string) => void;
  endTime: string;
  onEndTimeChange: (time: string) => void;
}

export function OneTimeDateTimeStep({
  date,
  onDateChange,
  startTime,
  onStartTimeChange,
  endTime,
  onEndTimeChange,
}: OneTimeDateTimeStepProps) {
  const { t } = useTranslation('staff');
  const [calendarOpen, setCalendarOpen] = useState(false);

  const timeError = startTime >= endTime;

  return (
    <div className="p-6 space-y-6">
      {/* Date selection */}
      <div className="space-y-3">
        <Label variant="section">
          {t('availability.dialog.date', 'Date')}
        </Label>
        <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className={cn(
                'w-full min-h-[56px] justify-start text-left font-normal text-base',
                !date && 'text-muted-foreground'
              )}
            >
              <CalendarIcon className="mr-3 h-5 w-5" />
              {date
                ? format(date, 'EEEE, MMM d, yyyy', { locale: getDateLocale() })
                : t('availability.dialog.pickDate', 'Pick a date')}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="single"
              selected={date || undefined}
              onSelect={(d) => {
                onDateChange(d || null);
                setCalendarOpen(false);
              }}
              disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
              initialFocus
              locale={getDateLocale()}
            />
          </PopoverContent>
        </Popover>
      </div>

      {/* Time selection */}
      <div className="space-y-3">
        <Label variant="section">
          {t('availability.wizard.steps.time', 'Time')}
        </Label>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>
              {t('availability.dialog.startTime', 'Start Time')}
            </Label>
            <TimePicker
              value={startTime}
              onChange={onStartTimeChange}
              placeholder={t('availability.dialog.selectStartTime', 'Select start time')}
            />
          </div>

          <div className="space-y-2">
            <Label>
              {t('availability.dialog.endTime', 'End Time')}
            </Label>
            <TimePicker
              value={endTime}
              onChange={onEndTimeChange}
              placeholder={t('availability.dialog.selectEndTime', 'Select end time')}
            />
          </div>
        </div>

        {timeError && (
          <p className="text-sm text-red-600">
            {t('availability.errors.endTimeAfterStart', 'End time must be after start time')}
          </p>
        )}
      </div>
    </div>
  );
}
